import { BuilderBlocks } from '@builder.io/react';
import { BuilderBlock } from '@builder.io/react/dist/types/src/components/builder-block.component';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import './sticky-cta.css';

interface StickyCTAProps {
  content?: ReactNode;
  marginTopDesktop: number | null;
  scrollThreshold: number | null;
  builderBlock: BuilderBlock;
  attributes: {
    style: any;
    className: string;
    'builder-id': string;
    id: string;
  };
}

export function StickyCTA({
  content,
  marginTopDesktop,
  scrollThreshold,
  builderBlock,
  attributes
}: StickyCTAProps) {
  const stickyCtaRef = useRef<HTMLDivElement>(null);
  const [initialBottoms, setInitialBottoms] = useState<{
    chatButtonBottom: number;
    backToTopButtonBottom: number;
  }>({ chatButtonBottom: 0, backToTopButtonBottom: 0 });
  const [initialMarginBottom, setInitialMarginBottom] = useState<number>(0);

  const adjustUtilityMenu = useCallback(() => {
    if (stickyCtaRef.current) {
      const stickyCtaRect = stickyCtaRef.current.getBoundingClientRect();
      const stickyCtaHeight = stickyCtaRect.height;

      const chatButtonParent = document.querySelector('#sticky-utility-menu .snap-chat')?.parentElement;
      const backToTopButtonParent = document.querySelector('#sticky-utility-menu .back-to-top')?.parentElement;
      const commonParent = chatButtonParent?.parentElement;
      if (!chatButtonParent || !backToTopButtonParent || !commonParent) return;

      const backToTopButtonRect = backToTopButtonParent.getBoundingClientRect();
      const overlap =
        Math.max(0, backToTopButtonRect.bottom - stickyCtaRect.top) + (marginTopDesktop || 0);

      if (window.innerWidth < 990) {
        const commonParentMarginBottom =
          initialMarginBottom || parseFloat(window.getComputedStyle(commonParent).marginBottom);

        if (!initialMarginBottom) {
          setInitialMarginBottom(commonParentMarginBottom);
        }

        commonParent.style.marginBottom = `${stickyCtaHeight}px`;
      } else {
        const chatButtonBottom =
          initialBottoms.chatButtonBottom ||
          parseFloat(window.getComputedStyle(chatButtonParent).bottom);
        const backToTopButtonBottom =
          initialBottoms.backToTopButtonBottom ||
          parseFloat(window.getComputedStyle(backToTopButtonParent).bottom);

        if (!initialBottoms.chatButtonBottom || !initialBottoms.backToTopButtonBottom) {
          setInitialBottoms({ chatButtonBottom, backToTopButtonBottom });
        }

        if (overlap > 0) {
          chatButtonParent.style.bottom = `${chatButtonBottom + overlap}px`;
          backToTopButtonParent.style.bottom = `${backToTopButtonBottom + overlap}px`;
        } else {
          chatButtonParent.style.bottom = `${chatButtonBottom}px`;
          backToTopButtonParent.style.bottom = `${backToTopButtonBottom}px`;
        }
      }
    }
  }, [marginTopDesktop, initialBottoms, initialMarginBottom]);

  useEffect(() => {
    adjustUtilityMenu();

    window.addEventListener('resize', adjustUtilityMenu);
    return () => {
      window.removeEventListener('resize', adjustUtilityMenu);
    };
  }, [adjustUtilityMenu]);

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            if (node.querySelector('#sticky-utility-menu .snap-chat') || node.querySelector('#sticky-utility-menu .back-to-top')) {
              adjustUtilityMenu();
            }
          }
        });
      });
    });

    const config = { childList: true, subtree: true };

    observer.observe(document.body, config);

    const stickyBar = document.querySelector('.sticky-cta') as HTMLElement;
    const theFooter = document.querySelector('[data-name="checkout-footer"]') as HTMLElement;
    const obswerver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            ( entry.isIntersecting === true && stickyBar && stickyBar.style)
            ? stickyBar.style.position = 'relative'
            : stickyBar.style.position = 'fixed'
        });
        });
        theFooter !== null ? obswerver.observe(theFooter) : null;
        stickyBar?.classList.add('sticky-cta-observe');
    if(scrollThreshold) {
        window.addEventListener('scroll', () => {
            if (window.scrollY > scrollThreshold) {
                stickyBar.style.bottom = '0'
            } else {
                stickyBar.style.bottom = '-100%'
            }
            });
    }

    return () => {
      observer.disconnect();
      obswerver.disconnect();
    };
  }, [adjustUtilityMenu, scrollThreshold]);

  return (
    <div
      ref={stickyCtaRef}
      className={`${attributes.className} sticky-cta bottom-0 z-10 w-full`}
      style={{ ...attributes.style}}
      builder-id={attributes['builder-id']}
      id={attributes.id}
    >
      <BuilderBlocks
        parentElementId={builderBlock.id}
        dataPath="component.options.content"
        blocks={content}
      />
    </div>
  );
}
